/**
 * Die Looks — als Rechnung, nicht als Datei.
 *
 * Die .cube-Dateien in `assets/luts` sind aus genau diesen Funktionen
 * erzeugt (`scripts/generate-luts.mjs`). Bis heute standen die Funktionen
 * nur dort, und die Vorschau hatte daneben eine zweite Fassung als
 * SVG-Filter — „dieselbe Rechnung, wo sie linear ist, eine Näherung, wo die
 * LUT nach Helligkeit biegt“. Eine Näherung ist ein Bild, das im Export
 * anders aussieht als am Schnittplatz.
 *
 * Jetzt stehen sie hier, einmal: der Erzeuger schreibt daraus die Dateien für
 * ffmpeg, und die Leinwand (7.69c) lädt daraus eine 3D-Textur für die
 * Grafikkarte. Beide rechnen dann wirklich dasselbe — und dass der Erzeuger
 * dieselben Dateien schreibt wie vorher, prüft ein Test Byte für Byte.
 */
const clamp = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
/** Filmische S-Kurve: hebt den Fuss ein wenig an und rollt die Schulter ab. */
const contrastCurve = (v, amount) => clamp(lerp(v, v * v * (3 - 2 * v), amount));
export const LOOKS = {
    'teal-orange': (r, g, b) => {
        const l = lum(r, g, b);
        const shadow = 1 - l;
        return [
            contrastCurve(clamp(r + 0.06 * l - 0.03 * shadow), 0.45),
            contrastCurve(clamp(g + 0.01 * l), 0.45),
            contrastCurve(clamp(b - 0.05 * l + 0.1 * shadow), 0.45),
        ];
    },
    warm: (r, g, b) => [clamp(r * 1.06 + 0.02), clamp(g * 1.01), clamp(b * 0.93)],
    cool: (r, g, b) => [clamp(r * 0.94), clamp(g * 0.99), clamp(b * 1.07 + 0.02)],
    bw: (r, g, b) => {
        const l = contrastCurve(lum(r, g, b), 0.35);
        return [l, l, l];
    },
    'flat-log': (r, g, b) => [lerp(r, 0.5, 0.35), lerp(g, 0.5, 0.35), lerp(b, 0.5, 0.35)],
    vivid: (r, g, b) => {
        const l = lum(r, g, b);
        const s = 1.35;
        return [
            contrastCurve(clamp(l + (r - l) * s), 0.3),
            contrastCurve(clamp(l + (g - l) * s), 0.3),
            contrastCurve(clamp(l + (b - l) * s), 0.3),
        ];
    },
    identity: (r, g, b) => [r, g, b],
};
/** Kantenlänge der Würfel, wie in den Dateien (`LUT_3D_SIZE 17`). */
export const LUT_GROESSE = 17;
/**
 * Der Würfel als Liste, Rot am schnellsten, dann Grün, dann Blau — die
 * Reihenfolge der .cube-Datei, und zugleich die, in der die Grafikkarte eine
 * 3D-Textur entgegennimmt (x zuerst).
 */
export function lutWuerfel(name, groesse = LUT_GROESSE) {
    const look = LOOKS[name];
    if (!look)
        return null;
    const raus = new Float32Array(groesse * groesse * groesse * 3);
    let i = 0;
    for (let bi = 0; bi < groesse; bi++) {
        for (let gi = 0; gi < groesse; gi++) {
            for (let ri = 0; ri < groesse; ri++) {
                const [r, g, b] = look(ri / (groesse - 1), gi / (groesse - 1), bi / (groesse - 1));
                raus[i++] = r;
                raus[i++] = g;
                raus[i++] = b;
            }
        }
    }
    return raus;
}
/** Derselbe Würfel als Text, Zeile für Zeile so, wie ffmpeg ihn liest. */
export function lutDatei(name, groesse = LUT_GROESSE) {
    const w = lutWuerfel(name, groesse);
    if (!w)
        return null;
    const zeilen = [`TITLE "${name}"`, `LUT_3D_SIZE ${groesse}`, 'DOMAIN_MIN 0.0 0.0 0.0', 'DOMAIN_MAX 1.0 1.0 1.0', ''];
    for (let i = 0; i < w.length; i += 3) {
        zeilen.push(`${w[i].toFixed(6)} ${w[i + 1].toFixed(6)} ${w[i + 2].toFixed(6)}`);
    }
    return `${zeilen.join('\n')}\n`;
}
/**
 * Wie dunkel die Vignette an einer Stelle macht, 0..1 — die Rechnung aus
 * ffmpeg (`vf_vignette.c`, „natural“): der Abstand zur Mitte, geteilt durch
 * den Abstand zur Ecke, und davon `cos(Winkel · Abstand)^4`. Der Winkel ist
 * `π/5 · (0,5 + Stärke)`, so schreibt ihn der Export.
 *
 * `dx`/`dy` sind Anteile der halben Breite bzw. Höhe *des Rahmens*, an dem
 * gemessen wird (`vignetteRahmen`): die Ecke liegt bei (1, 1), die Mitte bei
 * (0, 0). Ein Bild, das eingepasst in einem höheren Rahmen liegt, reicht mit
 * `dy` nur so weit, wie es hoch ist.
 *
 * `seiten` ist das Seitenverhältnis des Rahmens in Punkten (Breite durch
 * Höhe). Ohne Angabe wird quadratisch gerechnet -- die Abkürzung, die nur
 * für einen quadratischen Rahmen stimmt.
 */
export function vignetteFaktor(dx, dy, amount, seiten = 1) {
    const dnorm = Math.hypot(dx * seiten, dy) / Math.hypot(seiten, 1);
    if (dnorm > 1)
        return 0;
    const c = Math.cos((Math.PI / 5) * (0.5 + amount) * dnorm);
    return c * c * c * c;
}
//# sourceMappingURL=looks.js.map